import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Circle } from "lucide-react";
import type { OnboardingChecklist } from "../../api/types";

/** The getting-started steps and where each one is completed. Shared by the
 * dashboard card ({@link GettingStartedChecklist}) and the help-menu modal so
 * the list stays in one place. Feature-gated steps (catalog, employees) are
 * omitted from the backend payload when disabled and are not rendered. */
const CHECKLIST_ITEMS: Array<{
  key: "has_customer" | "has_product" | "has_sale" | "has_teammate" | "has_goal" | "has_logo";
  to: string;
}> = [
  { key: "has_customer", to: "/customers" },
  { key: "has_product", to: "/catalog" },
  { key: "has_sale", to: "/sales" },
  { key: "has_teammate", to: "/employees" },
  { key: "has_goal", to: "/goals" },
  { key: "has_logo", to: "/settings" },
];

/** The step rows (checkbox + title + "do it" link). Presentational — the
 * container owns the header/progress chrome. `onNavigate` lets a container
 * (e.g. the modal) close itself when a step link is followed. */
export function ChecklistItems({
  checklist,
  onNavigate,
  className = "space-y-2",
}: {
  checklist: OnboardingChecklist;
  onNavigate?: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <ul className={className}>
      {CHECKLIST_ITEMS.filter(({ key }) => key in checklist).map(({ key, to }) => {
        const done = Boolean(checklist[key]);
        return (
          <li
            key={key}
            className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm bg-white/70 dark:bg-slate-800/50 ring-1 ${
              done
                ? "ring-emerald-200 dark:ring-emerald-800/40"
                : "ring-slate-200/70 dark:ring-slate-800"
            }`}
          >
            {done ? (
              <CheckCircle2 size={18} className="text-emerald-500 flex-none" />
            ) : (
              <Circle size={18} className="text-slate-300 dark:text-slate-600 flex-none" />
            )}
            <span
              className={`flex-1 ${
                done
                  ? "text-fg-secondary line-through"
                  : "text-fg-primary font-medium"
              }`}
            >
              {t(`onboarding.checklist.items.${key}.title`)}
            </span>
            {!done && (
              <Link
                to={to}
                onClick={onNavigate}
                className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline flex-none"
              >
                {t(`onboarding.checklist.items.${key}.cta`)} →
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}
